import React from "react";
import DateField from "./DateField";
import type { Props as FieldProps } from "../Form/Field";
import type { DateValue } from "react-aria";
import { useDatePicker } from "react-aria";
import type { DatePickerStateOptions } from "react-stately";
import { useDatePickerState } from "react-stately";
import ButtonIconV2 from "../ButtonIconV2";
import { CalendarLine } from "../IconV2/react";
import { styled } from "@linaria/react";
import { Dialog, Popover } from "react-aria-components";
import Calendar from "./Calendar";
import { color, shadows } from "..";
// import TimeSelector from "../TimeField/TimeSelector";
import { Time } from "@internationalized/date";

export const DialogWrapper = styled.div`
  display: flex;
  background-color: ${color.hydrogen};
  border: 1px solid ${color.titanium100};
  border-radius: 4px;
  /* margin-top: 4px; */

  box-shadow: ${shadows.shadow200};
`;

export interface DatePickerProps<T extends DateValue> extends Omit<FieldProps, "afterInputSlot"> {
  dateOptions?: Omit<DatePickerStateOptions<T>, "hourCycle">;
  isNonModal?: boolean;
  /** Whether to show the button that clears the selected date. */
  clearable?: boolean;
}

function DatePicker<T extends DateValue>({
  dateOptions = {},
  isNonModal = true,
  clearable = true,
  ...fieldProps
}: DatePickerProps<T>) {
  const defaultOptions: DatePickerStateOptions<T> = React.useMemo(
    () => ({
      shouldCloseOnSelect: true,
      hourCycle: 24,
      granularity: "day",
    }),
    [],
  );

  const mergedOptions = React.useMemo(
    () => ({
      ...defaultOptions,
      ...dateOptions,
    }),
    [dateOptions, defaultOptions],
  );

  const state = useDatePickerState(mergedOptions);

  const triggerRef = React.useRef(null);

  const {
    fieldProps: ariaFieldProps,
    buttonProps,
    dialogProps,
    calendarProps,
  } = useDatePicker(
    {
      ...dateOptions,
      label: fieldProps.label,
      granularity: "day",
    },
    state,
    triggerRef,
  );

  delete buttonProps.onPress;

  React.useEffect(() => {
    if (
      mergedOptions.granularity !== undefined &&
      ["hour", "minute", "second"].includes(mergedOptions.granularity) &&
      state.timeValue === null
    ) {
      state.setTimeValue(new Time(0, 0, 0));
    }
  }, [mergedOptions.granularity, state]);

  const closeOnClickOutside = React.useCallback(
    (e: MouseEvent) => {
      if (!(e.target as HTMLElement)?.closest(".PickerContainer") && state.isOpen)
        state.setOpen(false);
    },
    [state],
  );

  React.useEffect(() => {
    window.addEventListener("click", closeOnClickOutside, { passive: true, once: true });
    return () => window.removeEventListener("click", closeOnClickOutside);
  }, [closeOnClickOutside]);

  return (
    <div ref={triggerRef} className="PickerContainer">
      <DateField
        {...fieldProps}
        clearable={clearable}
        dateOptions={{
          ...ariaFieldProps,
          hideTimeZone: true,
        }}
        afterInputSlot={
          <ButtonIconV2
            {...buttonProps}
            onClick={e => {
              e.stopPropagation();
              state.setOpen(true);
            }}
          >
            <CalendarLine />
          </ButtonIconV2>
        }
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={state.isOpen}
        placement="bottom end"
        isNonModal={isNonModal}
        // onOpenChange={state.setOpen}
        shouldUpdatePosition
        shouldCloseOnInteractOutside={() => true}
        offset={4}
      >
        <Dialog {...dialogProps}>
          <DialogWrapper>
            <Calendar {...calendarProps} />
            {/* issue with scroll into view */}
            {/* <TimeSelector
              dateFieldOptions={ariaFieldProps}
              value={state.timeValue ?? undefined}
              setTimeValue={state.setTimeValue}
            />*/}
          </DialogWrapper>
        </Dialog>
      </Popover>
    </div>
  );
}

export default DatePicker;
